<script setup lang="ts">
import type { ContentBlockInlineLink } from '@nimbus/types'
import { omit } from 'es-toolkit'
import { getQuery, parseURL } from 'ufo'
import type { NuxtLinkProps } from '#app'
import { computed, useRequestURL } from '#imports'

const props = defineProps<ContentBlockInlineLink>()

const requestUrl = useRequestURL()

// Content links to the brand's own domain are often written as absolute URLs
// (https://www.spilnu.dk/...), so only a different host counts as leaving the site.
function isOffsiteUrl(url: string) {
    const { protocol, host } = parseURL(url)
    if (protocol !== 'http:' && protocol !== 'https:') return false

    const stripWww = (value: string) => value.toLowerCase().replace(/^www\./, '')

    return stripWww(host ?? '') !== stripWww(requestUrl.host)
}

function getTo() {
    if (typeof props.url === 'string') {
        // The CMS editor did not allow starting urls with ?, which makes it impossible to write query params correctly.
        // This tweak allows it to start with /?, but strips the /, so it updates query params for the current route
        if (props.url.startsWith('/?')) {
            const query = getQuery(props.url)

            return {
                query
            }
        }
    }

    return props.url
}

const linkProps = computed(() => {
    const to = getTo()

    const value: NuxtLinkProps = {
        ...omit(props, ['type', 'children', 'url', 'noPrefetch']),
        to,
        external: typeof to === 'string',
        ...((props.newTabLinks || (typeof to === 'string' && isOffsiteUrl(to))) && { target: '_blank' })
    }

    return value
})
</script>

<template>
    <NuxtLink v-bind="linkProps" tabindex="-1">
        <NimbusContentBlockRenderer :content="children" />
    </NuxtLink>
</template>

<style scoped lang="scss">
</style>
